import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TranslateIcon from '@mui/icons-material/Translate';
import EditIcon from '@mui/icons-material/Edit';

import { VpIcon } from '@vp-ui/components';
import config from '@src/config';
import {
  useCaptionPolling,
  useTranslateCaption,
  useFetchCaptionText,
  useUpdateCaptionText
} from '@src/hooks/react-query/useCaptionsData';
import { useVpSnackbar } from '@vp-ui/hooks/useVpSnackbar';
import { CAPTION_LANGUAGES } from '@src/constants/caption-languages';
import { selectedChipSx } from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/captionStyles';

const VpMediaInfoTranslation = ({ media }) => {
  const [translateLang, setTranslateLang] = useState('');
  const [selectedTranslationId, setSelectedTranslationId] = useState(null);
  const [captionText, setCaptionText] = useState('');
  const [isTextDirty, setIsTextDirty] = useState(false);
  const mediaId = media?.mediaId;

  const { data: captionsResponse, isLoading } = useCaptionPolling(mediaId);
  const { mutate: translateCaptionMutate, isLoading: isTranslating } = useTranslateCaption(mediaId);
  const { mutate: updateCaptionTextMutate, isLoading: isSaving } = useUpdateCaptionText(mediaId);
  const { addSnackbarError, addSnackbarSuccess, addSnackbarInfo } = useVpSnackbar();

  const captions = Array.isArray(captionsResponse) ? captionsResponse : [];
  const originalCaption = captions.find((c) => !c.isTranslation && c.status === 'completed');
  const translations = useMemo(
    () => captions.filter((c) => c.isTranslation && c.status === 'completed'),
    [captions]
  );
  const processingTranslations = useMemo(
    () => captions.filter((c) => c.isTranslation && (c.status === 'pending' || c.status === 'processing')),
    [captions]
  );
  const isProcessing = captions.some((c) => c.status === 'pending' || c.status === 'processing');

  const selectedTranslation = selectedTranslationId
    ? translations.find((c) => c.id === selectedTranslationId)
    : null;

  const existingLanguages = captions
    .filter((c) => c.status === 'completed' || c.status === 'processing')
    .map((c) => c.targetLanguage);

  const availableLanguages = CAPTION_LANGUAGES.filter(
    (lang) => !existingLanguages.includes(lang.code)
  );

  const { data: captionTextData } = useFetchCaptionText(selectedTranslation?.id);

  useEffect(() => {
    const text = captionTextData?.text || captionTextData || '';
    setCaptionText(text);
    setIsTextDirty(false);
  }, [captionTextData]);

  useEffect(() => {
    if (translations.length > 0 && !selectedTranslationId) {
      setSelectedTranslationId(translations[0].id);
    }
  }, [translations, selectedTranslationId]);

  const handleTranslate = () => {
    if (!translateLang || !originalCaption) return;
    translateCaptionMutate(
      { captionId: originalCaption.id, targetLanguage: translateLang },
      {
        onSuccess: () => {
          const langLabel = CAPTION_LANGUAGES.find((l) => l.code === translateLang)?.label || translateLang;
          addSnackbarInfo({ message: `Translating to ${langLabel}. This may take a few minutes.` });
          setTranslateLang('');
        },
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  const handleSaveCaptionText = () => {
    if (!selectedTranslation) return;
    const trimmedText = captionText.trim();
    if (!trimmedText) {
      addSnackbarInfo({
        message: 'Translation text cannot be empty',
        severity: 'error',
        title: 'Error'
      });
      return;
    }
    updateCaptionTextMutate(
      { captionId: selectedTranslation.id, text: trimmedText },
      {
        onSuccess: () => {
          addSnackbarSuccess({ message: 'Translation saved.' });
          setIsTextDirty(false);
        },
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (!originalCaption) {
    return (
      <Typography variant="body2" color="text.secondary">
        You need to generate captions first before creating translations. Go to the Captions tab to get started.
      </Typography>
    );
  }

  return (
    <Stack gap={3}>
      {availableLanguages.length > 0 && (
        <Stack direction="row" gap={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={translateLang}
              onChange={(e) => setTranslateLang(e.target.value)}
              displayEmpty
              size="small"
            >
              <MenuItem value="" disabled>Select language...</MenuItem>
              {availableLanguages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>{lang.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={handleTranslate}
            disabled={!translateLang || isTranslating}
            startIcon={isTranslating ? <CircularProgress size={16} /> : <TranslateIcon />}
          >
            {isTranslating ? 'Translating...' : 'Process Translation'}
          </Button>
          {/* TODO: replace with translate-specific help center URL when available */}
          <Tooltip title="Learn more about translations">
            <IconButton
              component="a"
              href={config.helpCenterLink}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <VpIcon icon="HelpCircle" size="18px" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {(translations.length > 0 || processingTranslations.length > 0) && (
        <Stack gap={1}>
          <Typography variant="body2" color="text.secondary">Available Translations</Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {translations.map((c) => {
              const lang = CAPTION_LANGUAGES.find((l) => l.code === c.targetLanguage);
              const isSelected = selectedTranslationId === c.id;
              return (
                <Chip
                  key={c.id}
                  label={lang?.label || c.targetLanguage}
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedTranslationId(c.id)}
                  sx={isSelected ? selectedChipSx : undefined}
                />
              );
            })}
            {processingTranslations.map((c) => {
              const lang = CAPTION_LANGUAGES.find((l) => l.code === c.targetLanguage);
              return (
                <Chip
                  key={c.id}
                  label={lang?.label || c.targetLanguage}
                  icon={<CircularProgress size={12} />}
                  variant="outlined"
                  size="small"
                  disabled
                />
              );
            })}
          </Stack>
        </Stack>
      )}

      {translations.length === 0 && !isProcessing && (
        <Typography variant="body2" color="text.secondary">
          No translations yet. Select a language above and click Translate to get started.
        </Typography>
      )}

      {selectedTranslation && (
        <Stack gap={1}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" fontWeight={500}>Translation Text</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
              — edit to correct translation errors
            </Typography>
          </Stack>
          <TextField
            multiline
            minRows={5}
            maxRows={10}
            value={captionText}
            onChange={(e) => {
              setCaptionText(e.target.value);
              setIsTextDirty(true);
            }}
            placeholder="Translation text will appear here..."
            size="small"
            fullWidth
          />
          {isTextDirty && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveCaptionText}
              disabled={isSaving}
              sx={{ alignSelf: 'flex-end' }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          )}
          <Typography variant="body2" color="text.secondary">
            Now go to the Caption tab to process a video with your translation(s).
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

VpMediaInfoTranslation.propTypes = {
  media: PropTypes.shape({
    mediaId: PropTypes.string,
    mediaUrl: PropTypes.string,
    thumbnailUrl: PropTypes.string
  })
};

export default VpMediaInfoTranslation;



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoTranslation.jsx